<script setup lang="ts">
import type { CommentIdentity } from '~/types/blog'

type VerifyIconType = 'official-verify' | 'personal-verify' | 'big-vip'

const props = withDefaults(
  defineProps<{
    identity?: CommentIdentity | null
    size?: 'normal' | 'small'
  }>(),
  {
    identity: null,
    size: 'normal'
  }
)

const iconTypes: VerifyIconType[] = ['official-verify', 'personal-verify', 'big-vip']
const iconTitleMap: Record<VerifyIconType, string> = {
  'official-verify': '蓝色闪电',
  'personal-verify': '橙色闪电',
  'big-vip': '大会员'
}

const rawVerifyIcon = computed(() => {
  const raw = props.identity?.customFields?.verifyIcon
  if (raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, 'value')) {
    return (raw as { value?: unknown }).value
  }
  return raw
})

const verifyIcon = computed<VerifyIconType | ''>(() => {
  const value = String(rawVerifyIcon.value || '').trim()
  return iconTypes.includes(value as VerifyIconType) ? value as VerifyIconType : ''
})
</script>

<template>
  <span
    v-if="verifyIcon"
    class="comment-verify-icon"
    :class="[verifyIcon, `size-${size}`]"
    role="img"
    :aria-label="iconTitleMap[verifyIcon]"
    :title="iconTitleMap[verifyIcon]"
  />
</template>

<style scoped>
.comment-verify-icon {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: block;
  width: 16px;
  height: 16px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  pointer-events: none;
}

.comment-verify-icon.size-small {
  right: -1px;
  bottom: -1px;
  width: 13px;
  height: 13px;
}

.official-verify {
  background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB0PSIxNTk0Mjk2OTA0NjU3IiBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHAtaWQ9IjQ4ODQiIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iNjQiIGhlaWdodD0iNjQiPjxkZWZzPjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+PC9zdHlsZT48L2RlZnM+PHBhdGggZD0iTTUxMiA1MTJtLTUxMiAwYTUxMiA1MTIgMCAxIDAgMTAyNCAwIDUxMiA1MTIgMCAxIDAtMTAyNCAwWiIgZmlsbD0iI0ZGRkZGRiIgcC1pZD0iNDg4NSI+PC9wYXRoPjxwYXRoIGQ9Ik01MTIgNTEybS00MTguOTA5MDkxIDBhNDE4LjkwOTA5MSA0MTguOTA5MDkxIDAgMSAwIDgzNy44MTgxODIgMCA0MTguOTA5MDkxIDQxOC45MDkwOTEgMCAxIDAtODM3LjgxODE4MiAwWiIgZmlsbD0iIzRBQzdGRiIgcC1pZD0iNDg4NiI+PC9wYXRoPjxwYXRoIGQ9Ik00NzQuMTEyIDc2My45NTA1NDVhMzMuMjggMzMuMjggMCAwIDEtMTQuMDU2NzI3IDQuMDQ5NDU1IDIxLjAzODU0NSAyMS4wMzg1NDUgMCAwIDEtMjEuMzE3ODE4LTIwLjgwNTgxOCAxOS41NDkwOTEgMTkuNTQ5MDkxIDAgMCAxIDEuMjEwMTgxLTUuODY0NzI3bDQwLjQ5NDU0Ni0xODcuNDM4NTQ2LTE2Ny42NTY3MjcgMC4zMjU4MThhMjEuMjI0NzI3IDIxLjIyNDcyNyAwIDAgMS0yMS44NzYzNjQtMjAuODA1ODE4IDIwLjQzMzQ1NSAyMC40MzM0NTUgMCAwIDEgOS40OTUyNzMtMTYuODQ5NDU0bDI1MC4xODE4MTgtMjU1Ljc2NzI3M2M0LjYwOC0yLjc5MjcyNyA5LjU0MTgxOC00Ljc0NzYzNiAxNC42NjE4MTgtNC43OTQxODJhMjEuMTMxNjM2IDIxLjEzMTYzNiAwIDAgMSAyMS4zNjQzNjQgMjAuODUyMzY0IDE5Ljk2OCAxOS45NjggMCAwIDEtMi43OTI3MjggOS43NzQ1NDVsLTQwLjIxNTI3MiAxODQuMzJoMTY3Ljg0MjkwOWEyMS4xNzgxODIgMjEuMTc4MTgyIDAgMCAxIDIxLjY0MzYzNiAyMC44NTIzNjQgMjAuMjkzODE4IDIwLjI5MzgxOCAwIDAgMS05LjQ5NTI3MyAxNi45NDI1NDVsLTI0OS40ODM2MzYgMjU1LjIwODcyN3oiIGZpbGw9IiNGRkZGRkYiIHAtaWQ9IjQ4ODciPjwvcGF0aD48L3N2Zz4=);
}

.personal-verify {
  background-image: url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB0PSIxNTk0Mjk2OTAwOTA1IiBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHAtaWQ9IjQ3NDgiIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iNjQiIGhlaWdodD0iNjQiPjxkZWZzPjxzdHlsZSB0eXBlPSJ0ZXh0L2NzcyI+PC9zdHlsZT48L2RlZnM+PHBhdGggZD0iTTUxMiA1MTJtLTUxMiAwYTUxMiA1MTIgMCAxIDAgMTAyNCAwIDUxMiA1MTIgMCAxIDAtMTAyNCAwWiIgZmlsbD0iI0ZGRkZGRiIgcC1pZD0iNDc0OSI+PC9wYXRoPjxwYXRoIGQ9Ik01MTIgNTEybS00MTguOTA5MDkxIDBhNDE4LjkwOTA5MSA0MTguOTA5MDkxIDAgMSAwIDgzNy44MTgxODIgMCA0MTguOTA5MDkxIDQxOC45MDkwOTEgMCAxIDAtODM3LjgxODE4MiAwWiIgZmlsbD0iI0ZGQzYyRSIgcC1pZD0iNDc1MCI+PC9wYXRoPjxwYXRoIGQ9Ik00NzQuMTEyIDc2My45NTA1NDVhMzMuMjggMzMuMjggMCAwIDEtMTQuMDU2NzI3IDQuMDQ5NDU1IDIxLjAzODU0NSAyMS4wMzg1NDUgMCAwIDEtMjEuMzE3ODE4LTIwLjgwNTgxOCAxOS41NDkwOTEgMTkuNTQ5MDkxIDAgMCAxIDEuMjEwMTgxLTUuODY0NzI3bDQwLjQ5NDU0Ni0xODcuNDM4NTQ2LTE2Ny42NTY3MjcgMC4zMjU4MThhMjEuMjI0NzI3IDIxLjIyNDcyNyAwIDAgMS0yMS44NzYzNjQtMjAuODA1ODE4IDIwLjQzMzQ1NSAyMC40MzM0NTUgMCAwIDEgOS40OTUyNzMtMTYuODQ5NDU0bDI1MC4xODE4MTgtMjU1Ljc2NzI3M2M0LjYwOC0yLjc5MjcyNyA5LjU0MTgxOC00Ljc0NzYzNiAxNC42NjE4MTgtNC43OTQxODJhMjEuMTMxNjM2IDIxLjEzMTYzNiAwIDAgMSAyMS4zNjQzNjQgMjAuODUyMzY0IDE5Ljk2OCAxOS45NjggMCAwIDEtMi43OTI3MjggOS43NzQ1NDVsLTQwLjIxNTI3MiAxODQuMzJoMTY3Ljg0MjkwOWEyMS4xNzgxODIgMjEuMTc4MTgyIDAgMCAxIDIxLjY0MzYzNiAyMC44NTIzNjQgMjAuMjkzODE4IDIwLjI5MzgxOCAwIDAgMS05LjQ5NTI3MyAxNi45NDI1NDVsLTI0OS40ODM2MzYgMjU1LjIwODcyN3oiIGZpbGw9IiNGRkZGRkYiIHAtaWQ9IjQ3NTEiPjwvcGF0aD48L3N2Zz4=);
}

.big-vip {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNTcgMTU3IiB3aWR0aD0iNjQiIGhlaWdodD0iNjQiPjxkZWZzPjxzdHlsZT4uYXtmaWxsOiNmZmY7fS5ie2ZpbGw6I2ZiNzI5OTt9PC9zdHlsZT48L2RlZnM+PGNpcmNsZSBjbGFzcz0iYSIgY3g9Ijc4LjUiIGN5PSI3OC41IiByPSI3OC41Ii8+PHBhdGggY2xhc3M9ImIiIGQ9Ik0xNS4wOCw3OUE2My43LDYzLjcsMCwxLDAsNzguNzcsMTUuMjloMEE2My43LDYzLjcsMCwwLDAsMTUuMDgsNzlaIi8+PHBhdGggY2xhc3M9ImEiIGQ9Ik0zOS45MSw2NC4yM2ExNDUuNjgsMTQ1LjY4LDAsMCwwLDI3LjY2LTMuNzEsMjEuODEsMjEuODEsMCwwLDAsLjc2LTExLjEzQTQ0LjQ3LDQ0LjQ3LDAsMCwwLDYzLjkyLDM4Yy0uNjgtLjUuOTQtMi4zNSw0LjkyLTIuMzVhMTcuNjcsMTcuNjcsMCwwLDEsMTQuNjgsOC40M2MzLDYsMCwxMy4xNS44NSwxMy45MXM3LjU1LTIuMjgsMTAuMS0zLjEyYTMwLjQxLDMwLjQxLDAsMCwxLDE2LjQ1LTEuNzcsMTMuNTMsMTMuNTMsMCwwLDEsNi4zNywxMy4xNWMtLjYsNS42NS01LjY5LDUuMzktMTEuNjMsNC4zOGE2NC4zOSw2NC4zOSwwLDAsMC0yMS4yOSwyLjUzYy0yLjcyLjkzLTQuNSwxLjQ0LTUuNzcsMy42M3MtMTIsMjYuNDctMTMuMzIsMjkuODRhMjMuNzEsMjMuNzEsMCwwLDEtOS45Miw5LjYyLDE2LjM4LDE2LjM4LDAsMCwxLTE0LjY4LS44NWMtNC4xNi0zLjItNS4yNi01LjY0LS43Ny03LjE2czEzLjIzLTEyLjQsMTUuNDUtMTUuNzYsMTAuMjYtMTQuNTksOC40OC0xNS43N0E0NC4yMyw0NC4yMywwLDAsMCw0OC40OSw3OC40Yy0yLjQ3Ljc2LTMuNjUsMS45NC01Ljc3LS43NmEyMy4yMywyMy4yMywwLDAsMS0zLjU2LTExLjM4Yy4yOC0xLjM1LjUzLTIsLjc1LTJabTQzLjQ5LDIxYzEuMTgtMSwzLjgyLS42Niw1LjM0LS41OGE2Mi4zOCw2Mi4zOCwwLDAsMSwxNC40Miw0LjE4YzQuNTgsMi4yNiwyMC4xLDcuNzcsMTcsMTcuNzJzLTQuNSwxMC43Ny03LjQ2LDguMzVTODQsODkuODEsODMuMjMsODguNTVhMi42NCwyLjY0LDAsMCwxLC4xNy0zLjM1WiIvPjwvc3ZnPg==);
}
</style>
